import React, { forwardRef, useCallback, useEffect, useId, useImperativeHandle } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import cx from 'classnames'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { BtnV2, l } from '@manychat/manyui'

import { useAppDispatch } from 'reduxTyped'
import {
  SubmitRef,
  SubmitResult,
} from 'apps/aiAutomation/components/EngagementGoalEditor/lib/types'
import { AiAutomationLayoutActions } from 'apps/aiAutomation/store/layout'
import { HookFormTextArea } from 'components/HookFormControls/HookFormTextArea/HookFormTextArea'
import { HookFormTextInput } from 'components/HookFormControls/HookFormTextInput/HookFormTextInput'
import { URL_HAS_PROTOCOL_REGEX, URL_RULE_WITH_OPTIONAL_PROTOCOL } from 'constants/RegExs'

import sharedCm from '../../../EditorLayout/styles.module.css'
import { ContextEditorFormValues } from '../../lib/types'

interface ContextEditorProps {
  onFinish: (data: ContextEditorFormValues) => void
  isActive: boolean
  contextData: ContextEditorFormValues | null
  onDirtyChange: (value: boolean) => void
}

export const ContextEditor = forwardRef<SubmitRef<ContextEditorFormValues>, ContextEditorProps>(
  ({ onFinish, isActive, contextData, onDirtyChange }, ref) => {
    const dispatch = useAppDispatch()

    const isFirstGeneration = !contextData?.context

    const {
      handleSubmit,
      formState,
      setValue,
      reset,
      control,
      resetField,
      formState: { dirtyFields },
    } = useForm<ContextEditorFormValues>({
      defaultValues: {
        url: contextData?.url ?? '',
        context: contextData?.context ?? '',
      },
      resolver: zodResolver(
        z.object({
          url: z
            .string()
            .min(1, l.translate('Enter link to continue'))
            .max(2000, l.translate('Link is too long'))
            .regex(URL_RULE_WITH_OPTIONAL_PROTOCOL, l.translate('Invalid link'))
            .transform((value) => {
              if (!URL_HAS_PROTOCOL_REGEX.test(value)) {
                return `https://${value}`
              }
              return value
            }),
          context: z.string().min(1, l.translate('Enter description to continue')),
        }),
      ),
    })

    const onCancel = () => {
      resetField('context', { defaultValue: contextData?.context ?? '' })
    }

    const onSubmit = useCallback(
      (data: ContextEditorFormValues) => {
        if (!isActive) {
          return
        }

        reset(data)

        onFinish(data)
      },
      [isActive, onFinish, reset],
    )

    useEffect(() => {
      if (contextData?.url) {
        setValue('url', contextData.url)
      }
    }, [contextData?.url, setValue])

    useEffect(() => {
      if (contextData?.context) {
        setValue('context', contextData.context)
      }
    }, [contextData?.context, setValue])

    useEffect(() => {
      if (isActive) {
        dispatch(AiAutomationLayoutActions.confirmChanged({ show: formState.isDirty }))
      } else {
        dispatch(AiAutomationLayoutActions.confirmChanged({ show: false }))
      }
    }, [dispatch, formState.isDirty, isActive])

    const formId = useId()

    useEffect(() => {
      onDirtyChange(Boolean(dirtyFields.context))
    }, [dirtyFields.context, onDirtyChange])

    useImperativeHandle(
      ref,
      () => ({
        submit: async () => {
          let result: SubmitResult<ContextEditorFormValues> = { isValid: false }

          await handleSubmit((data) => {
            result = { isValid: true, data }
            reset(data)
          })()
          return result
        },
      }),
      [handleSubmit, reset],
    )

    return (
      <section>
        <div className={sharedCm.section}>
          <h4 className={cx('m-b-xxs font-medium', !isActive && 'm-b')}>
            {l.translate('What are you promoting?')}
          </h4>

          {isActive && !contextData && (
            <p className="m-t-0 text-secondary">
              {l.translate('AI will use your input to generate a message.')}
            </p>
          )}

          <form id={formId} onSubmit={handleSubmit(onSubmit)} className="m-t-md">
            {isActive && (
              <label id="leadgen-link" className="font-medium d-block m-b-xs">
                {l.translate('<l10n context="Noun"/>Link')}
              </label>
            )}

            <HookFormTextInput
              control={control}
              name="url"
              disabled={!isActive}
              className={cx(isActive ? 'm-b-lg' : 'm-b')}
              ariaLabelledBy="leadgen-link"
              placeholder="https://manychat.com"
            />

            {isActive && (
              <label id="leadgen-context" className="font-medium d-block m-b-xs">
                {l.translate('What’s this link about?')}
              </label>
            )}

            <HookFormTextArea
              control={control}
              name="context"
              disabled={!isActive}
              ariaLabelledBy="leadgen-context"
              placeholder={l.translate(
                'Free masterclass on AI chat funnels, July 30. Great for small biz owners.',
              )}
              minRows={2}
              maxRows={6}
              helpText={
                isActive && !isFirstGeneration
                  ? l.translate('Changing this will regenerate the AI message')
                  : undefined
              }
            />
          </form>
        </div>

        {isActive && !contextData && (
          <div className="m-t-sm d-flex justify-end">
            <BtnV2 form={formId} type="submit" leftIcon="MagicWand">
              {l.translate('Generate message')}
            </BtnV2>
          </div>
        )}

        {contextData && dirtyFields.context && (
          <div className="m-t-sm d-flex justify-between">
            <BtnV2 onClick={onCancel} variant="ghost-light">
              {l.translate('Cancel')}
            </BtnV2>

            <BtnV2 form={formId} leftIcon="MagicWand" type="submit" variant={'default'}>
              {l.translate('Regenerate AI message')}
            </BtnV2>
          </div>
        )}
      </section>
    )
  },
)
